import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Close,
  Star,
  Event,
  Group,
  LocationOn,
  CalendarToday,
  TrendingUp,
  Cake,
  Wc,
} from '@mui/icons-material';
import { OrganizerStats, fetchOrganizerStats, GalleryMediaItem } from '../services/api';
import GalleryGrid from './gallery/GalleryGrid';
import GalleryLightbox from './gallery/GalleryLightbox';

interface OrganizerDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  organizerId: string;
  organizerName: string;
}

const OrganizerDetailsDialog: React.FC<OrganizerDetailsDialogProps> = ({
  open,
  onClose,
  organizerId,
  organizerName,
}) => {
  const [organizerData, setOrganizerData] = useState<OrganizerStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (open && organizerId) {
      loadOrganizerData();
    }
  }, [open, organizerId]);

  const loadOrganizerData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrganizerStats(organizerId);
      setOrganizerData(data);
      console.log('📊 Loaded organizer data:', data);
    } catch (err) {
      console.error('Failed to load organizer data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load organizer information');
    } finally {
      setLoading(false);
    }
  };

  const galleryItems = useMemo<GalleryMediaItem[]>(() => {
    if (!organizerData) return [];

    const fromGallery =
      organizerData.gallery && organizerData.gallery.length > 0
        ? organizerData.gallery
        : organizerData.galleryPreview || [];

    if (fromGallery.length > 0) {
      return fromGallery.filter((item) => !!item?.image_url);
    }

    if (organizerData.profilePictureUrl) {
      return [
        {
          id: -1,
          image_url: organizerData.profilePictureUrl,
          caption: organizerData.name
            ? `${organizerData.name}'s profile photo`
            : 'Profile photo',
          is_public: true,
          display_order: 1,
          created_at: '',
          updated_at: '',
        },
      ];
    }

    return [];
  }, [organizerData]);

  const handleOpenLightbox = (index: number) => {
    if (!galleryItems.length) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const genderLabel = useMemo(() => {
    if (!organizerData?.gender) {
      return null;
    }
    const genderMap: Record<string, string> = {
      female: 'Female',
      male: 'Male',
      non_binary: 'Non-binary',
      other: 'Other',
      prefer_not_to_say: 'Prefer not to say',
    };
    return genderMap[organizerData.gender] ?? organizerData.gender;
  }, [organizerData?.gender]);

  const calculatedAge = useMemo(() => {
    if (!organizerData) {
      return null;
    }

    if (typeof organizerData.age === 'number' && !Number.isNaN(organizerData.age)) {
      return organizerData.age;
    }

    if (organizerData.birthday) {
      const birthDate = new Date(organizerData.birthday);
      if (!Number.isNaN(birthDate.getTime())) {
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const hasHadBirthdayThisYear =
          today.getMonth() > birthDate.getMonth() ||
          (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
        if (!hasHadBirthdayThisYear) {
          age -= 1;
        }
        if (age >= 0 && age < 120) {
          return age;
        }
      }
    }

    return null;
  }, [organizerData]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '50vh', maxHeight: '90vh' }
        }}
      >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Organizer Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {organizerName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
          </Box>
        ) : organizerData ? (
          <Box>
            {/* Organizer Profile Card */}
            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 2 }}>
                  <Tooltip title={galleryItems.length ? 'Open gallery' : ''}>
                    <Avatar
                      src={organizerData.profilePictureUrl}
                      onClick={() => handleOpenLightbox(0)}
                      sx={{
                        width: 80,
                        height: 80,
                        cursor: galleryItems.length ? 'pointer' : 'default',
                      }}
                    >
                      {organizerData.name?.[0] || organizerData.username?.[0] || '?'}
                    </Avatar>
                  </Tooltip>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {organizerData.name}
                    </Typography>
                    
                    {organizerData.username && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        @{organizerData.username}
                      </Typography>
                    )}
                    
                    {(organizerData.city || organizerData.country) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {[organizerData.city, organizerData.country].filter(Boolean).join(', ')}
                        </Typography>
                      </Box>
                    )}

                    {(calculatedAge !== null || genderLabel) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {calculatedAge !== null && (
                          <Chip
                            icon={<Cake sx={{ fontSize: 16 }} />}
                            label={`Age: ${calculatedAge}`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        )}
                        {genderLabel && (
                          <Chip
                            icon={<Wc sx={{ fontSize: 16 }} />}
                            label={genderLabel}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        )}
                      </Box>
                    )}
                    
                    {organizerData.bio && (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                        "{organizerData.bio}"
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {galleryItems.length > 0 && (
              <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    Photo Gallery
                  </Typography>
                  <GalleryGrid
                    media={galleryItems}
                    onPhotoClick={handleOpenLightbox}
                    dense
                    variant="compact"
                  />
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center', py: 2 }}>
                  <Event color="primary" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {organizerData.created_activities_count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Activities Created
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center', py: 2 }}>
                  <Group color="success" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {organizerData.participations_count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Participations
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center', py: 2 }}>
                  <TrendingUp color="warning" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {organizerData.groups_count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Groups
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center', py: 2 }}>
                  <Star color="error" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {organizerData.average_received_rating?.toFixed(1) || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Average Rating
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Recent Activities */}
            {organizerData.recent_created_activities && organizerData.recent_created_activities.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event color="primary" />
                    Recent Activities
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {organizerData.recent_created_activities.map((activity, index) => (
                      <Box key={activity.id}>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {activity.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(activity.date)} • {formatTime(activity.time)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {activity.city}
                              </Typography>
                            </Box>
                            <Chip
                              label={`${activity.current_participants}/${activity.max_participants}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                        {index < organizerData.recent_created_activities.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Recent Participations */}
            {organizerData.recent_participations && organizerData.recent_participations.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Group color="success" />
                    Recent Participations
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {organizerData.recent_participations.map((participation, index) => (
                      <Box key={participation.activity.id}>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {participation.activity.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(participation.activity.date)} • {formatTime(participation.activity.time)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {participation.activity.city}
                              </Typography>
                            </Box>
                            <Chip
                              label={participation.status}
                              size="small"
                              color={participation.status === 'confirmed' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                        {index < organizerData.recent_participations.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Groups */}
            {organizerData.groups && organizerData.groups.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp color="warning" />
                    Groups
                  </Typography>
                  <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                    {organizerData.groups.map((group, index) => (
                      <Box key={group.id}>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {group.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            {group.description && (
                              <Typography variant="caption" color="text.secondary">
                                {group.description}
                              </Typography>
                            )}
                            <Chip
                              label={`${group.member_count} members`}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                        {index < organizerData.groups.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No organizer information available
            </Typography>
          </Box>
        )}
      </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <GalleryLightbox
        open={lightboxOpen}
        media={galleryItems}
        initialIndex={lightboxIndex}
        onClose={handleCloseLightbox}
      />
    </>
  );
};

export default OrganizerDetailsDialog; 